import { FieldModel, PracticeContentDataFields } from '@cibo/core'
import { usePracticesContent } from '@cibo/landmanager'
import Info from '@mui/icons-material/InfoOutlined'
import LandscapeOutlinedIcon from '@mui/icons-material/LandscapeOutlined'
import { IconButton, ListSubheader, MenuItem, Stack, Typography, useTheme } from '@mui/material'
import { sortBy, uniq } from 'ramda'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMultiselectCheckboxContext } from '../../../features/ResourceDetailFeatures/EQIP/MultiselectCheckboxFieldTable/MultiselectCheckboxContext'
import { MultiselectMenuItem } from '../../../features/ResourceDetailFeatures/EQIP/MultiselectCheckboxFieldTable/MultiselectFieldInput'
import { useRecommendedFieldPractices } from '../../../hooks'
import { usePracticeDrawer } from '../../PracticeDrawer'
import { useEqipProgramEngagementPractices } from '../../PracticesList'
import { iconMap } from '../../PracticesList/iconMap'

type PracticeId = string

export const useOptionsDisplay = ({ engagementId }: { engagementId: string }) => {
  const { palette } = useTheme()
  const { t } = useTranslation('@cibo/programs/FieldsSelection')
  const { setDrawerState } = usePracticeDrawer()
  const practiceContent = usePracticesContent()
  const recommendedPractices = useRecommendedFieldPractices(engagementId)
  const allPractices = useEqipProgramEngagementPractices({
    engagementId,
  })
  const { fieldAnswers, combinedAnswer, options, optionDisplayString } =
    useMultiselectCheckboxContext()

  const initialSelected: string[] = useMemo(() => {
    if (allPractices.isLoading) return []
    const selectedPractices =
      allPractices.practices
        .filter(practice => {
          return combinedAnswer[practice.fields.practiceId]?.value === 'checked'
        })
        .map(practice => practice.fields.practiceId) || []
    return selectedPractices
  }, [
    allPractices.practiceContentQuery.dataUpdatedAt,
    allPractices.detailsQuery.dataUpdatedAt,
    allPractices.isPending,
  ])

  const sortByInitialSelected = useCallback(
    (practiceIds: string[], bulk: boolean, field: boolean, alpha?: boolean) => {
      const practiceIdsAlpha = alpha
        ? sortBy((pid: PracticeId) => optionDisplayString(pid) || '', practiceIds)
        : practiceIds
      return practiceIdsAlpha.sort((a, b) => {
        if (bulk) {
          const statusSortValue = (practice: PracticeId) => {
            const answer = combinedAnswer[practice]?.value
            if (answer === 'checked') return 0
            if (answer === 'indeterminate') return 1
            return 2 // unchecked
          }
          return statusSortValue(a) - statusSortValue(b)
        } else if (field) {
          const aSelected = initialSelected?.includes(a)
          const bSelected = initialSelected?.includes(b)
          if (aSelected && !bSelected) return -1
          if (!aSelected && bSelected) return 1
          return 0
        }
        return 0
      })
    },
    [initialSelected, optionDisplayString]
  )

  const onOpenDrawer = (practiceId: PracticeId) =>
    setDrawerState({
      open: true,
      practice: practiceContent.data?.items.find(
        p => (p.fields.practiceId as any as PracticeId) === practiceId
      ) as unknown as PracticeContentDataFields,
      selectInteraction: false,
    })

  const optionsOverride = useCallback(
    ({ bulk }: { bulk: boolean }) =>
      (props: { field?: FieldModel }) => {
        const { field } = props || {}
        const optionsList = bulk
          ? recommendedPractices.data?.fields &&
            uniq(
              Object.values(recommendedPractices.data?.fields).flatMap(
                fieldRecommendation => fieldRecommendation.pools
              )
            )
          : field && recommendedPractices.data?.fields?.[field.resourceId].pools

        return [
          <MenuItem key="none" value="none">
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Typography>{t('selectPractices')}</Typography>
              <Typography variant="caption" color={palette.secondary.dark}>
                {t('clearAll')}
              </Typography>
            </Stack>
          </MenuItem>,
          optionsList?.map(pool => {
            const Symbol =
              iconMap[pool.rankingPool.symbol as keyof typeof iconMap] || LandscapeOutlinedIcon
            const selectedPractices =
              options.filter(practice => {
                if (!bulk && field) {
                  return fieldAnswers[field.resourceId]?.result.includes(practice)
                } else if (bulk) {
                  const answer = combinedAnswer[practice]?.value
                  return answer === 'checked' || answer === 'indeterminate'
                } else {
                  return false
                }
              }) || []

            let sortedPoolPractices: PracticeId[] = []
            const selectedPoolPractices = pool.practicePoints
              .filter(practice => selectedPractices.includes(practice.practice.code))
              .map(p => p.practice.code)

            if (selectedPractices.length > 4) {
              sortedPoolPractices = selectedPoolPractices
            } else {
              const practicesToSort = uniq([
                ...selectedPoolPractices,
                ...pool.practicePoints.map(p => p.practice.code),
              ])

              sortedPoolPractices = sortByInitialSelected(
                practicesToSort.slice(0, 4),
                bulk,
                !!field
              )
            }

            // send as an array otherwise the interactions don't work!
            return [
              <ListSubheader key={pool.rankingPool.label} sx={{ paddingBlock: '15px' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Symbol />
                  <Typography color="text.primary">
                    {t('fundingPool', { pool: pool.rankingPool.label })}
                  </Typography>
                </Stack>
              </ListSubheader>,
              sortedPoolPractices.map((practice: PracticeId, index: number) => (
                <MenuItem
                  key={`choice-${pool.rankingPool.label}-${practice}`}
                  data-testid={`choice-${practice}`}
                  value={practice}
                  style={{
                    paddingLeft: '15px',
                    paddingBlock: 0,
                    backgroundColor: palette.background.paper,
                    borderBottom:
                      index !== sortedPoolPractices.length - 1
                        ? `1px solid ${palette.divider}`
                        : 'none',
                  }}
                >
                  <MultiselectMenuItem
                    key={`choice-${pool.rankingPool.label}-${practice}`}
                    typeValues={
                      !bulk && field ? { type: 'field', row: field } : { type: 'combined' }
                    }
                    stale={false}
                    option={practice}
                    listItemTextProps={{
                      primaryTypographyProps: { sx: { fontSize: '14px ' } },
                    }}
                    InfoElement={() => (
                      <IconButton
                        color="info"
                        onClick={event => {
                          event.stopPropagation()
                          event.preventDefault()
                          onOpenDrawer(practice)
                        }}
                      >
                        <Info />
                      </IconButton>
                    )}
                  />
                </MenuItem>
              )),
            ]
          }),
          <ListSubheader key={'all'} sx={{ paddingBlock: '15px' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography color="text.primary">{t('allOtherPractices')}</Typography>
            </Stack>
          </ListSubheader>,
          sortByInitialSelected(
            allPractices.practices.map(a => a.fields.practiceId),
            bulk,
            !!field,
            true
          ).map((practice: string, index: number) => (
            <MenuItem
              key={`choice-all-${practice}`}
              data-testid={`choice-${practice}`}
              value={practice}
              style={{
                paddingLeft: '15px',
                paddingBlock: 0,
                backgroundColor: palette.background.paper,
                borderBottom:
                  index !== allPractices.practices.length - 1
                    ? `1px solid ${palette.divider}`
                    : 'none',
              }}
            >
              <MultiselectMenuItem
                key={`choice-all-${practice}`}
                typeValues={!bulk && field ? { type: 'field', row: field } : { type: 'combined' }}
                stale={false}
                option={practice}
                listItemTextProps={{
                  primaryTypographyProps: { sx: { fontSize: '14px ' } },
                }}
                InfoElement={() => (
                  <IconButton
                    color="info"
                    onClick={event => {
                      event.stopPropagation()
                      event.preventDefault()
                      onOpenDrawer(practice)
                    }}
                  >
                    <Info />
                  </IconButton>
                )}
              />
            </MenuItem>
          )),
        ]
      },
    [
      allPractices.practiceContentQuery.dataUpdatedAt,
      allPractices.detailsQuery.dataUpdatedAt,
      allPractices.isPending,
      sortByInitialSelected,
    ]
  )

  return { optionsOverride, isReady: !allPractices.isLoading }
}
